import ApiAutocomplete from '../../components/ConfigurationForm/ApiAutocomplete';
import RetailerAutocomplete from '../../components/ConfigurationForm/RetailerAutocomplete';
import { Option } from '../../components/field-editors/Option';
import OptionFieldEditor from '../../components/field-editors/OptionFieldEditor';
import DynamicOptionFieldEditor from '../../components/field-editors/DynamicOptionFieldEditor';

import TextFieldEditor from '../../components/field-editors/TextFieldEditor';
import URLFieldEditor from '../../components/field-editors/URLFieldEditor';
import DefaultRuleComponent from '../../components/rule-components/DefaultRuleComponent';
import EarnAndBurnRuleComponent from '../../components/rule-components/EarnAndBurnRuleComponent';
import useOptions from '../constants/Options';
import useTSHacks from '../tshacks/tshacks';
import { nonNegativeNumberValidator } from '../validators/NonNegativeNumberValidator';
import ApiService from '../../services/ApiService';
import { AxiosPromise } from 'axios';
import { FormikValues } from 'formik';
import { Market } from '../../gen/api';

const { getEarnAndBurnOptions, getEarnBurnOrderOptions, getRoundingOptions } = useOptions();

const { getObjectProperty } = useTSHacks();

export type WidthType = boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | undefined;

const DEFAULT_VALUE: any = undefined;

export class RuleConfig {
    constructor(
        readonly order: number = 1000,
        readonly width: WidthType = 6,
        readonly component: any = DefaultRuleComponent,
        readonly render: boolean = true,
    ) {}
}

export class FactConfig {
    constructor(
        readonly order: number = 1000,
        readonly width: WidthType = 6,
        readonly component: any = TextFieldEditor,
        readonly props: {
            render: boolean;
            type: string;
            options?: () => Option[];
            validator?: (value: any) => string | undefined;
            codelist?: string;
            autocompleteOptionsMethod?: (searchTerm?: string, values?: FormikValues) => AxiosPromise<Array<any>>;
            getByIdMethod?: (id: number) => AxiosPromise<any>;
            multiple?: boolean;
            defaultValue?: any;
        } = {render: true, type: "text"}
    ) {}
}

export const config = {
    ruleConfigs: {
        ['DEFAULT']: new RuleConfig(),
        ['optin.included.participants']: new RuleConfig(0),
        ['optin.rules.earnburn']: new RuleConfig(2, 6, EarnAndBurnRuleComponent),
        ['optin.rewards.display']: new RuleConfig(3),
        ['optin.excluded.offers']: new RuleConfig(1),
        ['optin.partner.attributes']: new RuleConfig(4),
    },
    customFactInputs: {
        /** optin.included.participants */
        ['optin.included.participants.allParticipantsIncludedIn']: new FactConfig(
            1, 
            12, 
            ApiAutocomplete, 
            { 
                render: true, 
                type: 'text', 
                autocompleteOptionsMethod: (searchTerm?: string, values?: FormikValues) => {return ApiService().fetchMarkets(searchTerm)},
                getByIdMethod: (id: number) => {return ApiService().fetchMarketById(id)}, 
                multiple: true 
            }
        ),
        ['optin.included.participants.notIncludedParticipants']: new FactConfig(
            2, 
            12, 
            ApiAutocomplete, 
            { 
                render: true, 
                type: 'text', 
                autocompleteOptionsMethod: (searchTerm?: string, values?: FormikValues) => {
                    let marketIds = [];

                    if (values) {
                        marketIds = values['RULE-'.concat('optin.included.participants').concat('-FACT-').concat('allParticipantsIncludedIn').replace(/[.]/g, '@DOT@')].map((item: Market) => item.id)
                    }

                    return ApiService().fetchRetailersByMarketIds(marketIds, searchTerm)
                },
                getByIdMethod: (id: number) => {return ApiService().fetchRetailerById(id)},
                multiple: true 
            }
        ),
        ['optin.included.participants.alsoIncludedParticipants']: new FactConfig(
            3, 
            12, 
            ApiAutocomplete, 
            { 
                render: true, 
                type: 'text', 
                autocompleteOptionsMethod: (searchTerm?: string, values?: FormikValues) => {return ApiService().fetchRetailers(searchTerm)}, 
                getByIdMethod: (id: number) => {return ApiService().fetchRetailerById(id)},
                multiple: true 
            }
        ),
        /** optin.excluded.offers */
        ['optin.excluded.offers.allOffersSponsoredBy']: new FactConfig(1, 12, RetailerAutocomplete),
        ['optin.excluded.offers.notExcludedOffers']: new FactConfig(
            2, 
            12, 
            ApiAutocomplete, 
            { 
                render: true, 
                type: 'text', 
                autocompleteOptionsMethod: (searchTerm?: string, values?: FormikValues) => {return ApiService().fetchOffers(searchTerm)}, 
                getByIdMethod: (id: number) => {return ApiService().fetchOfferById(id)},
                multiple: true 
            }
        ),
        ['optin.excluded.offers.alsoExcludedOffers']: new FactConfig(
            3, 
            12, 
            ApiAutocomplete, 
            { 
                render: true, 
                type: 'text', 
                autocompleteOptionsMethod: (searchTerm?: string, values?: FormikValues) => {return ApiService().fetchOffers(searchTerm)}, 
                getByIdMethod: (id: number) => {return ApiService().fetchOfferById(id)},
                multiple: true 
            }
        ),
        /** optin.rules.earnburn */
        ['optin.rules.earnburn.earnOrBurn']: new FactConfig(
            0,
            12,
            OptionFieldEditor,
            {
                render: true,
                type: "text",
                options: getEarnAndBurnOptions
            }
        ),
        ['optin.rules.earnburn.earnBurnOrder']: new FactConfig(
            1,
            12,
            OptionFieldEditor,
            {
                render: true,
                type: "text",
                options: getEarnBurnOrderOptions
            }
        ),
        ['optin.rules.earnburn.earnLocalCurrencyUnit']: new FactConfig(
            2,
            6,
            DynamicOptionFieldEditor,
            {
                render: true,
                type: "text",
                codelist: "CurrencyType"
            }
        ),
        ['optin.rules.earnburn.earnLocalCurrencyRate']: new FactConfig(
            3,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "number",
                validator: nonNegativeNumberValidator
            }
        ),
        ['optin.rules.earnburn.earnForeignCurrencyUnit']: new FactConfig(
            4,
            6,
            DynamicOptionFieldEditor,
            {
                render: true,
                type: "text",
                codelist: "CurrencyType"
            }
        ),
        ['optin.rules.earnburn.earnForeignCurrencyRate']: new FactConfig(
            5,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "number",
                validator: nonNegativeNumberValidator
            }
        ),
        ['optin.rules.earnburn.earnRoundingSettings']: new FactConfig(
            6,
            12,
            OptionFieldEditor,
            {
                render: true,
                type: "number",
                options: getRoundingOptions
            }
        ),
        ['optin.rules.earnburn.burnLocalCurrencyUnit']: new FactConfig(
            7,
            6,
            DynamicOptionFieldEditor,
            {
                render: true,
                type: "text",
                codelist: "CurrencyType"
            }
        ),
        ['optin.rules.earnburn.burnLocalCurrencyRate']: new FactConfig(
            8,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "number",
                validator: nonNegativeNumberValidator
            }
        ),
        ['optin.rules.earnburn.burnForeignCurrencyUnit']: new FactConfig(
            9,
            6,
            DynamicOptionFieldEditor,
            {
                render: true,
                type: "text",
                codelist: "CurrencyType"
            }
        ),
        ['optin.rules.earnburn.burnForeignCurrencyRate']: new FactConfig(
            10,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "number",
                validator: nonNegativeNumberValidator
            }
        ),
        ['optin.rules.earnburn.burnRoundingSettings']: new FactConfig(
            11,
            12,
            OptionFieldEditor,
            {
                render: true,
                type: "number",
                options: getRoundingOptions
            }
        ),
        ['optin.rules.earnburn.burnRewardRedemptionLimit']: new FactConfig(
            12,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "number",
                validator: nonNegativeNumberValidator
            }
        ),
        ['optin.rules.earnburn.burnMinimumSourceAmount']: new FactConfig(
            13,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "number",
                validator: nonNegativeNumberValidator
            }
        ),
        /** optin.rewards.display  */
        ['optin.rewards.display.partnerTitle']: new FactConfig(1, 6),
        ['optin.rewards.display.partnerDescription']: new FactConfig(2, 6),
        ['optin.rewards.display.partnerTerms']: new FactConfig(3, 12),
        ['optin.rewards.display.partnerImageUrl']: new FactConfig(4, 12, URLFieldEditor),
        ['optin.rewards.display.earnTitle']: new FactConfig(5, 6),
        ['optin.rewards.display.earnDescription']: new FactConfig(6, 6),
        ['optin.rewards.display.earnImageUrl']: new FactConfig(7, 12, URLFieldEditor),
        ['optin.rewards.display.burnTitle']: new FactConfig(8, 6),
        ['optin.rewards.display.burnDescription']: new FactConfig(9, 6),
        ['optin.rewards.display.burnImageUrl']: new FactConfig(10, 12, URLFieldEditor),
        /** optin.partner.attributes */
        ['optin.partner.attributes.partnerCodeStandard']: new FactConfig(
            1,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "text",
            }
        ),
        ['optin.partner.attributes.partnerCodeAcquisition']: new FactConfig(
            2,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "text",
            }
        ),
        ['optin.partner.attributes.transactionTypeStandard']: new FactConfig(
            3,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "text",
            }
        ),
        ['optin.partner.attributes.transactionTypeAcquisition']: new FactConfig(
            4,
            6,
            TextFieldEditor,
            {
                render: true,
                type: "text",
            }
        ),
        ['optin.fis.attributes.rewardProgramIdentifier']: new FactConfig(
            1,
            12,
            TextFieldEditor,
            {
                render: true,
                type: "text",
            }
        ),
        ['optin.fis.attributes.offerId']: new FactConfig(
            2,
            12,
            ApiAutocomplete,
            {
                render: true,
                type: 'text',
                autocompleteOptionsMethod: (searchTerm?: string, values?: FormikValues) => {return ApiService().fetchOffers(searchTerm)},
                getByIdMethod: (id: number) => {return ApiService().fetchOfferById(id)},
                multiple: true
            }
        ),
    },

    standardFactInputs: {
        ['DEFAULT']: new FactConfig(),
        ['UrlValue']: new FactConfig(DEFAULT_VALUE, 12, URLFieldEditor),
    },
};

export const getFactDisplayConfig = (longFactCode: string, factType: string) => {
    return (
        getObjectProperty(config.customFactInputs, longFactCode) ||
        getObjectProperty(config.standardFactInputs, factType) ||
        config.standardFactInputs.DEFAULT
    );
};

export const getRuleDisplayConfig = (ruleCode: string) => {
    return getObjectProperty(config.ruleConfigs, ruleCode) || config.ruleConfigs.DEFAULT;
};

